import { mergeAttributes, Node } from "@tiptap/core"

// URL validation regex for HTTPS URLs
export const IFRAME_URL_REGEX =
	/^https:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(\.[a-zA-Z]{2,})(\/.*)?$/

export const isValidIframeUrl = (url: string): boolean => {
	if (!url) return false

	// Must start with https://
	if (!url.startsWith("https://")) {
		return false
	}

	// Validate URL format
	try {
		const urlObj = new URL(url)

		// Block Productlane domains to prevent same-origin attacks
		const hostname = urlObj.hostname.toLowerCase()
		if (
			hostname.endsWith(".productlane.com") ||
			hostname === "productlane.com"
		) {
			return false
		}

		return IFRAME_URL_REGEX.test(url)
	} catch {
		return false
	}
}

export interface IframeOptions {
	/**
	 * Controls if the iframe should be allowed to go fullscreen.
	 * @default true
	 */
	allowFullscreen: boolean

	/**
	 * The HTML attributes for an iframe node.
	 * @default {}
	 */
	HTMLAttributes: Record<string, unknown>
}

/**
 * The options for setting an iframe.
 */
interface SetIframeOptions {
	src: string
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		iframe: {
			/**
			 * Insert an iframe embed
			 * @param options The iframe attributes
			 * @example editor.commands.setIframe({ src: 'https://example.com' })
			 */
			setIframe: (options: SetIframeOptions) => ReturnType
		}
	}
}

/**
 * This extension adds support for embedding external content via iframes.
 * Security:
 * - Only HTTPS URLs are allowed
 * - Productlane domains are blocked to prevent same-origin attacks
 * - Sandbox allows scripts, forms, popups, and same-origin for third-party embeds
 * - Domain blocking ensures iframe cannot access parent page even with allow-same-origin
 */
export const IframeExtension = Node.create<IframeOptions>({
	name: "iframe",

	addOptions() {
		return {
			allowFullscreen: true,
			HTMLAttributes: {},
		}
	},

	group: "block",

	atom: true,

	draggable: true,

	addAttributes() {
		return {
			src: {
				default: null,
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: "div[data-iframe-embed] iframe",
			},
			// The server-side DOM cannot evaluate the descendant selector above, so
			// an embed only survives a parse there through this plain tag rule.
			{
				tag: "iframe",
				getAttrs: (element) => {
					const src = element.getAttribute("src") ?? ""
					if (element.hasAttribute("data-pdf") || src.endsWith(".pdf")) {
						return false
					}
					return { src }
				},
			},
		]
	},

	addCommands() {
		return {
			setIframe:
				(options: SetIframeOptions) =>
				({ commands }) => {
					if (!isValidIframeUrl(options.src)) {
						return false
					}

					return commands.insertContent({
						type: this.name,
						attrs: options,
					})
				},
		}
	},

	renderHTML({ HTMLAttributes }) {
		const embedUrl = HTMLAttributes.src as string

		return [
			"div",
			{
				"data-iframe-embed": "",
				class: "aspect-video",
			},
			[
				"iframe",
				mergeAttributes(this.options.HTMLAttributes, {
					src: embedUrl,
					frameborder: "0",
					allowfullscreen: this.options.allowFullscreen,
					allow: this.options.allowFullscreen ? "fullscreen" : undefined,
					sandbox:
						"allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox",
				}),
			],
		]
	},
})
